
import { useState } from "react";
import { Course } from "@/types/course"; 
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { approveCourse, rejectCourse } from "@/utils/adminUtils";
import { 
  AlertDialog,
  AlertDialogContent, 
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction 
} from "@/components/ui/alert-dialog";

interface PendingCoursesTabProps {
  pendingCourses: Course[];
  onCoursesUpdate: (courses: Course[]) => void;
}

const PendingCoursesTab = ({ pendingCourses, onCoursesUpdate }: PendingCoursesTabProps) => {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [courseToReject, setCourseToReject] = useState<Course | null>(null);
  const [processing, setProcessing] = useState<string | null>(null);
  const [errorInfo, setErrorInfo] = useState<{message: string} | null>(null);
  const { toast } = useToast();

  const handleApprove = async (courseId: string) => {
    try {
      setProcessing(courseId);
      console.log("Approving course with ID:", courseId);
      
      const updatedCourses = await approveCourse(courseId);
      console.log("Courses after approval:", updatedCourses);
      
      // Update UI with the latest courses
      onCoursesUpdate(updatedCourses);
      
      // Show success message
      toast({
        title: "Course Approved",
        description: "The course has been successfully approved.",
      });
    } catch (error) {
      console.error("Error in handleApprove:", error);
      
      // Show detailed error in UI
      setErrorInfo({
        message: error instanceof Error 
          ? error.message 
          : "Failed to approve the course. Please try again."
      });
      
      toast({
        title: "Error",
        description: "Failed to approve the course. Please check console for details.",
        variant: "destructive",
      });
    } finally {
      setProcessing(null);
      setDialogOpen(false);
    }
  };

  const handleRejectClick = (course: Course) => {
    setCourseToReject(course);
    setRejectionReason("");
    setRejectionDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!courseToReject || !rejectionReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for rejection.",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(courseToReject.id);
      console.log("Rejecting course with ID:", courseToReject.id);
      
      // First get course details and update with rejection reason
      const { data: courseData, error: courseError } = await supabase
        .from('courses')
        .select('id, title, mentor_id, status')
        .eq('id', courseToReject.id)
        .single();
        
      if (courseError) {
        console.error("Error fetching course details:", courseError);
        throw new Error(`Course fetch failed: ${courseError.message}`);
      }
      
      if (!courseData) {
        console.error("Course not found:", courseToReject.id);
        throw new Error("Course not found");
      }
      
      // Update course status to rejected and store rejection reason
      const { error } = await supabase
        .from('courses')
        .update({ 
          status: 'rejected',
          rejection_reason: rejectionReason.trim()
        })
        .eq('id', courseToReject.id);

      if (error) {
        console.error("Error rejecting course:", error);
        throw error;
      }

      // Create notification for the mentor with custom reason
      try {
        const { error: notificationError } = await supabase
          .from('notifications')
          .insert({
            receiver_id: courseData.mentor_id,
            type: 'course_rejected',
            content: `Your course "${courseData.title}" has been rejected. Reason: ${rejectionReason.trim()}`,
            link: `/my-academy?courseId=${courseToReject.id}`,
            read: false
          });

        if (notificationError) {
          console.error("Error creating notification:", notificationError);
        } else {
          console.log("✅ Notification created successfully for mentor:", courseData.mentor_id);
        }
      } catch (notificationErr) {
        console.error("Exception creating notification:", notificationErr);
      }
      
      // Reload courses to update UI
      const updatedCourses = await rejectCourse(courseToReject.id);
      console.log("Courses after rejection:", updatedCourses);
      
      // Update UI with the latest courses
      onCoursesUpdate(updatedCourses);
      
      // Show success message
      toast({
        title: "Course Rejected",
        description: "The course has been successfully rejected and the mentor has been notified.",
      });
      
      setRejectionDialogOpen(false);
      setCourseToReject(null);
      setRejectionReason("");
    } catch (error) {
      console.error("Error in handleRejectConfirm:", error);
      
      // Show detailed error in UI
      setErrorInfo({
        message: error instanceof Error 
          ? error.message 
          : "Failed to reject the course. Please try again."
      });
      
      toast({
        title: "Error",
        description: "Failed to reject the course. Please check console for details.",
        variant: "destructive",
      });
    } finally {
      setProcessing(null);
    }
  };

  const viewCourseDetails = (course: Course) => {
    setSelectedCourse(course);
    setDialogOpen(true);
  };

  const getAuthorName = (course: Course) => {
    if (course.author && course.author.includes(' ')) {
      return course.author;
    }
    
    return course.author || course.instructor || "Unknown Author";
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Pending Courses</CardTitle>
          <CardDescription>
            Review and approve courses submitted by mentors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingCourses && pendingCourses.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Course Title</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingCourses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell className="font-medium">{course.title}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        course.courseType === 'subscription' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {course.courseType === 'subscription' 
                          ? 'Subscription' 
                          : 'Voice/Video Call'}
                      </span>
                    </TableCell>
                    <TableCell>{getAuthorName(course)}</TableCell>
                    <TableCell>${course.price || 0}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex items-center"
                          onClick={() => viewCourseDetails(course)}
                        >
                          <span>View Details</span>
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex items-center text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200"
                          onClick={() => handleApprove(course.id)}
                          disabled={processing === course.id}
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          {processing === course.id ? 'Processing...' : 'Approve'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex items-center text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => handleRejectClick(course)}
                          disabled={processing === course.id}
                        >
                          <XCircle className="mr-1 h-4 w-4" />
                          {processing === course.id ? 'Processing...' : 'Reject'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-8 text-center text-gray-500">
              No pending courses at this time.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Course Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{selectedCourse?.title}</DialogTitle>
            <DialogDescription>
              Course details submitted by mentor
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Course Type</h4>
              <p className="text-sm">
                {selectedCourse?.courseType === 'subscription' 
                  ? 'Subscription Based Mentoring' 
                  : 'Voice/Video Call Mentoring'}
              </p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Description</h4>
              <p className="text-sm">{selectedCourse?.description || "No description provided"}</p>
            </div>
            
            {selectedCourse?.duration && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">Duration</h4>
                <p className="text-sm">{selectedCourse.duration} minutes</p>
              </div>
            )}
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Price</h4>
              <p className="text-sm">${selectedCourse?.price || 0}</p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Course ID</h4>
              <p className="text-sm text-gray-500 break-all">{selectedCourse?.id}</p>
            </div>
            
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Submitted At</h4>
              <p className="text-sm">{selectedCourse && selectedCourse.createdAt ? new Date(selectedCourse.createdAt).toLocaleString() : "Unknown date"}</p>
            </div>
          </div>
          <div className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-600"
              onClick={() => {
                if (selectedCourse) {
                  setDialogOpen(false);
                  handleRejectClick(selectedCourse);
                }
              }}
              disabled={processing !== null}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Reject
            </Button>
            <Button 
              variant="default"
              onClick={() => {
                if (selectedCourse) {
                  handleApprove(selectedCourse.id);
                  setDialogOpen(false);
                }
              }}
              disabled={processing !== null}
            >
              <CheckCircle className="mr-1 h-4 w-4" />
              Approve
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Dialog */}
      <Dialog open={rejectionDialogOpen} onOpenChange={setRejectionDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Reject Course: {courseToReject?.title}</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this course. This will be sent to the mentor.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter the reason for rejection (required)..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <Button 
              variant="outline"
              onClick={() => setRejectionDialogOpen(false)}
              disabled={processing !== null}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleRejectConfirm}
              disabled={processing !== null || !rejectionReason.trim()}
            >
              <XCircle className="mr-1 h-4 w-4" />
              {processing ? 'Processing...' : 'Reject Course'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Error dialog */}
      <AlertDialog open={!!errorInfo} onOpenChange={(open) => !open && setErrorInfo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center text-red-600">
              <AlertCircle className="h-5 w-5 mr-2" />
              Error Updating Course
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>{errorInfo?.message}</p>
              <div className="bg-gray-100 p-3 rounded-md text-xs font-mono">
                <p>Possible issues:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li>Missing database permissions or Row Level Security policy</li>
                  <li>Network connectivity issues</li>
                  <li>The course may already be approved or rejected</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PendingCoursesTab;
